import { Box, chakra, InputElement } from '@chakra-ui/react';
import {
  InvoiceApplicationFormDataInput,
  InvoiceApplicationFormDataRowInput,
  InvoiceStatusEnum,
  SelectionStateEnum,
} from '@monetize/types/app';
import {
  getCurrencyDecimals,
  getCurrencySymbol,
  toDateShort,
} from '@monetize/utils/core';
import { Controller, UseFormReturn } from 'react-hook-form';
import { InvoiceStatusEnumDisplay } from '../../constants/invoice';
import { getInvoiceDetailRoute } from '../../constants/routes';
import { roundNumberToDecimal } from '../../utils/numberUtils';
import {
  MCenter,
  MCheckbox,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MText,
  MTooltip,
} from '../Monetize';
import { ItemWithLinkInNewWindow } from '../Monetize/ItemWithLinkInNewWindow';
import { MCurrency } from '../Monetize/MCurrency';
import { MEntityStatusBadge } from '../Monetize/MEntityStatusBadge';
import { useTransactionApplicationRow } from './useTransactionApplicationRow';

interface TransactionApplicationTableRowProps {
  index: number;
  formState: UseFormReturn<InvoiceApplicationFormDataInput>;
  row: InvoiceApplicationFormDataRowInput;
  isDisabled?: boolean;
  /**
   * Currently unapplied amount of payment or credit
   */
  availableAmount: number;
}

export const TransactionApplicationTableRow = ({
  index,
  formState,
  row,
  isDisabled,
  availableAmount,
}: TransactionApplicationTableRowProps) => {
  const { control } = formState;
  const { amountAppliedFromOtherTransactables, invoice } = row;
  const { amount, currency } = invoice;
  const { amountDue, handleToggleCheckAll } = useTransactionApplicationRow({
    index,
    formState,
    row,
    availableAmount,
  });

  return (
    <>
      <MCenter role="cell">
        <Controller
          name={`rows.${index}.checkedState`}
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <MCheckbox
              isChecked={value === SelectionStateEnum.ALL}
              isIndeterminate={value === SelectionStateEnum.SOME}
              isDisabled={isDisabled}
              id={`rows.${index}.checkedState`}
              onChange={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleToggleCheckAll();
              }}
              {...rest}
            />
          )}
        />
      </MCenter>
      <MFlex role="cell" align="center">
        <chakra.label
          htmlFor={`rows.${index}.checkedState`}
          fontWeight="400"
          m="0"
        >
          <ItemWithLinkInNewWindow url={getInvoiceDetailRoute(invoice.id)}>
            <MText>{invoice.invoiceNumber ?? invoice.id}</MText>
          </ItemWithLinkInNewWindow>
          <MText fontSize="xs">Due {toDateShort(invoice.dueDate)}</MText>
        </chakra.label>
      </MFlex>

      <MFlex role="cell" align="center" justify="flex-end">
        <MCurrency amount={amount} options={{ currency }} />
      </MFlex>

      <MFlex role="cell" align="center" justify="center">
        <MCurrency
          amount={roundNumberToDecimal(amountAppliedFromOtherTransactables)}
          options={{ currency }}
        />
      </MFlex>

      <MFormField role="cell" error={`rows.${index}.appliedAmount`}>
        <Controller
          name={`rows.${index}.appliedAmount`}
          control={control}
          render={({ field: { value, onChange, ...rest } }) => (
            <Box position="relative">
              <InputElement placement="start" w="4" pl="2">
                {getCurrencySymbol(invoice.currency)}
              </InputElement>
              <MCustomNumberInput
                value={value ?? 0}
                inputMode="decimal"
                placeholder={`0.00`}
                precision={getCurrencyDecimals(currency)}
                isDisabled={isDisabled}
                styleProps={{
                  pl: 5,
                  pr: 2,
                  textAlign: 'right',
                }}
                onChange={(valueAsString, valueAsNumber) =>
                  onChange(valueAsString)
                }
                {...rest}
              />
            </Box>
          )}
        />
      </MFormField>

      <MFlex role="cell" align="center" justify="flex-end">
        <MCurrency
          amount={amountDue}
          options={{ currency, maximumFractionDigits: 2 }}
        />
      </MFlex>

      {amountDue < 0 ? (
        <MTooltip label="The applied amount cannot exceed the amount due">
          <MText
            role="cell"
            color="tRed.base"
            alignSelf="center"
            justifySelf="flex-end"
          >
            Error
          </MText>
        </MTooltip>
      ) : (
        <MEntityStatusBadge
          role="cell"
          status={
            amountDue === 0
              ? InvoiceStatusEnumDisplay[InvoiceStatusEnum.PAID]
              : InvoiceStatusEnumDisplay[InvoiceStatusEnum.UNPAID]
          }
          alignSelf="center"
          justifySelf="flex-end"
        />
      )}

      <MDivider gridColumn="1/-1" />
    </>
  );
};
